import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import Logo from '../../components/common/Logo';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { trackEvent } from '../../utils/posthog';

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  
  const navigate = useNavigate();
  const { register } = useAuth();
  const { showToast } = useToast();
  
  useEffect(() => {
    // Track page view
    trackEvent('page_view', {
      page: 'register_page'
    });
  }, []);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Password strength indicator
  const getPasswordStrength = (password: string): { score: number; text: string; color: string } => {
    if (!password) return { score: 0, text: 'Very Weak', color: 'bg-gray-200' };
    
    let score = 0;
    
    // Length check
    if (password.length >= 8) score += 1;
    
    // Complexity checks
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    
    // Return score and text
    switch (score) {
      case 0:
        return { score, text: 'Very Weak', color: 'bg-red-500' };
      case 1:
        return { score, text: 'Weak', color: 'bg-orange-500' };
      case 2:
        return { score, text: 'Fair', color: 'bg-yellow-500' };
      case 3:
        return { score, text: 'Good', color: 'bg-blue-500' };
      case 4:
        return { score, text: 'Strong', color: 'bg-green-500' };
      default:
        return { score: 0, text: 'Very Weak', color: 'bg-red-500' };
    }
  };
  
  const passwordStrength = getPasswordStrength(formData.password);
  
  const validateForm = (): boolean => {
    // Validate name
    if (formData.name.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return false;
    }
    
    // Validate email
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    // Validate password
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    
    // Validate password match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    // Validate password strength
    if (passwordStrength.score < 2) {
      setError('Please choose a stronger password');
      return false;
    }
    
    // Validate terms agreement
    if (!agreeToTerms) {
      setError('You must agree to the Terms of Use to create an account');
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Track registration attempt
      trackEvent('signup_attempt', {
        email_domain: formData.email.split('@')[1]
      });
      
      const { success, error } = await register(
        formData.name, 
        formData.email, 
        formData.password
      );

      if (success) {
        // Track successful registration
        trackEvent('signup_success');
        
        showToast(
          'Registration successful! Please check your email to verify your account.',
          'success'
        );
        navigate('/login');
      } else {
        // Track failed registration
        trackEvent('signup_error', {
          error_message: error
        });
        
        setError(error || 'Failed to create account');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('An unexpected error occurred');
      
      // Track error
      trackEvent('signup_error', {
        error_type: 'unexpected',
        error_message: err.message
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#F9F7FE] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Link 
            to="/" 
            onClick={() => trackEvent('navigation_click', { link_name: 'logo', location: 'register_page' })}
          >
            <Logo />
          </Link>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Or{' '}
          <Link 
            to="/login" 
            className="font-medium text-[#7B4AE2] hover:text-[#6A3ACF]"
            onClick={() => trackEvent('navigation_click', { link_name: 'sign_in', location: 'register_page' })}
          >
            sign in to existing account
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-start">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Full name
              </label>
              <div className="mt-1">
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#7B4AE2] focus:border-[#7B4AE2] transition duration-150 ease-in-out text-sm"
                  placeholder="Your name"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#7B4AE2] focus:border-[#7B4AE2] transition duration-150 ease-in-out text-sm"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#7B4AE2] focus:border-[#7B4AE2] transition duration-150 ease-in-out text-sm pr-10"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              
              {/* Password strength indicator */}
              {formData.password && (
                <div className="mt-2">
                  <div className="flex justify-between items-center mb-1">
                    <div className="text-xs text-gray-500">Password strength: </div>
                    <div className={`text-xs font-medium`} style={{ color: passwordStrength.color }}>
                      {passwordStrength.text}
                    </div>
                  </div>
                  <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${passwordStrength.color}`} 
                      style={{ width: `${(passwordStrength.score / 4) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
              
              {/* Password requirements */}
              <ul className="mt-2 text-xs text-gray-500 space-y-1">
                <li className="flex items-center">
                  {formData.password.length >= 8 ? (
                    <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
                  ) : (
                    <span className="h-3 w-3 mr-1">•</span>
                  )}
                  At least 8 characters
                </li>
                <li className="flex items-center">
                  {/[A-Z]/.test(formData.password) ? (
                    <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
                  ) : (
                    <span className="h-3 w-3 mr-1">•</span>
                  )}
                  At least one uppercase letter
                </li>
                <li className="flex items-center">
                  {/[0-9]/.test(formData.password) ? (
                    <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
                  ) : (
                    <span className="h-3 w-3 mr-1">•</span>
                  )}
                  At least one number
                </li>
              </ul>
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm password
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#7B4AE2] focus:border-[#7B4AE2] transition duration-150 ease-in-out text-sm pr-10 ${
                    formData.confirmPassword && 
                    formData.confirmPassword !== formData.password 
                      ? 'border-red-300' 
                      : ''
                  }`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {formData.confirmPassword && 
               formData.confirmPassword !== formData.password && (
                <p className="mt-1 text-xs text-red-600">
                  Passwords do not match
                </p>
              )}
            </div>

            {/* Terms of Use Checkbox */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  checked={agreeToTerms}
                  onChange={() => {
                    setAgreeToTerms(!agreeToTerms);
                    trackEvent('checkbox_toggle', {
                      checkbox_name: 'terms_agreement',
                      checked: !agreeToTerms
                    });
                  }}
                  className="h-4 w-4 text-[#7B4AE2] focus:ring-[#7B4AE2] border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="font-medium text-gray-700">
                  I agree to the <Link 
                    to="/terms-of-use" 
                    className="text-[#7B4AE2] hover:underline" 
                    target="_blank"
                    onClick={() => trackEvent('navigation_click', { link_name: 'terms_of_use', location: 'register_page' })}
                  >Terms of Use</Link>
                </label>
                <p className="text-gray-500">
                  By creating an account, you agree to our Terms of Use and acknowledge that you have read our Privacy Policy.
                </p>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting || !agreeToTerms}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#7B4AE2] hover:bg-[#6A3ACF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7B4AE2] disabled:bg-[#9D78E9] disabled:cursor-not-allowed"
                onClick={() => {
                  if (!isSubmitting && agreeToTerms) {
                    trackEvent('button_click', { button_name: 'create_account', location: 'register_page' });
                  }
                }}
              >
                {isSubmitting ? 'Creating account...' : 'Create account'}
              </button>
            </div>
          </form>
          
          <div className="mt-6">
            <p className="text-xs text-center text-gray-500">
              By signing up, you agree to our{' '}
              <Link 
                to="/terms-of-use" 
                className="font-medium text-[#7B4AE2]"
                onClick={() => trackEvent('navigation_click', { link_name: 'terms_of_use', location: 'register_page_footer' })}
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link 
                to="/privacy-policy" 
                className="font-medium text-[#7B4AE2]"
                onClick={() => trackEvent('navigation_click', { link_name: 'privacy_policy', location: 'register_page_footer' })}
              >
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;